import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import StorefrontHeroActions from './components/StorefrontHeroActions';
import { useSiteSettings } from './SiteSettingsContext';
import PageSeoHelmet from './PageSeoHelmet';
import { SITE_SETTINGS_DEFAULTS } from './siteSettingsDefaults';
import { brandNameFromSettings, brandLogoSrcFromSettings } from './siteBrand';
import BrandLogo from './components/BrandLogo';
import { resolveMediaUrl } from './productMediaUtils';
import { resolveFeaturedProducts } from './featuredProducts';
import { useProducts } from './useProducts';
import './about-page.css';

function About() {
  const { settings: site } = useSiteSettings();
  const { allProducts, loading: catalogLoading } = useProducts();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const aboutCopy = site.page_copy?.about || {};
  const values = aboutCopy.values || [];
  const brandName = brandNameFromSettings(site);
  const brandLogo = brandLogoSrcFromSettings(site);
  const storyImage = useMemo(() => {
    const og = String(site.seo_home_og_image || '').trim();
    if (og) return resolveMediaUrl(og);
    if (catalogLoading && allProducts.length === 0) return '';
    const featured = resolveFeaturedProducts(allProducts, site.featured_products, 1);
    if (featured[0]?.image) return resolveMediaUrl(featured[0].image);
    const withImage = (allProducts || []).find((p) => String(p?.image || '').trim());
    return withImage ? resolveMediaUrl(withImage.image) : '';
  }, [allProducts, site.featured_products, site.seo_home_og_image, catalogLoading]);
  const tagline = site.about_tagline || SITE_SETTINGS_DEFAULTS.about_tagline;
  const heroTitle = site.about_hero_title || SITE_SETTINGS_DEFAULTS.about_hero_title;
  const heroSubtitle = site.about_hero_subtitle || SITE_SETTINGS_DEFAULTS.about_hero_subtitle;

  return (
    <div className="about-page store-page-enter">
      <PageSeoHelmet page="about" />

      <section
        className={`about-hero-banner${storyImage ? ' about-hero-banner--photo' : ''}`}
        aria-labelledby="about-page-title"
      >
        {storyImage ? (
          <>
            <img src={storyImage} alt="" className="about-hero-banner__img" loading="eager" />
            <div className="about-hero-banner__overlay" aria-hidden />
          </>
        ) : null}
        <div className="container store-page__container">
          <div className="about-hero-banner__inner">
            <p className="about-hero-banner__eyebrow">{brandName}</p>
            <h1 id="about-page-title" className="about-hero-banner__title">
              {heroTitle}
            </h1>
            {storyImage ? (
              tagline ? (
                <p className="about-hero-banner__tagline">&ldquo;{tagline}&rdquo;</p>
              ) : null
            ) : (
              <p className="about-hero-banner__subtitle">{heroSubtitle}</p>
            )}
            <StorefrontHeroActions
              placement="about"
              className="about-hero-banner__actions d-flex flex-wrap"
            />
          </div>
        </div>
      </section>

      <section className="about-story-section" aria-labelledby="about-story-heading">
        <div className="container store-page__container">
          <div className="about-story-card">
            <div className="row align-items-center g-4">
              <div className="col-md-4">
                <div className="about-story-card__logo-wrap">
                  <BrandLogo
                    src={brandLogo}
                    alt={brandName}
                    className="about-story-card__logo"
                    size={152}
                  />
                </div>
              </div>
              <div className="col-md-8">
                <h2 id="about-story-heading" className="about-story-card__title">
                  {aboutCopy.story_heading || 'Our story'}
                </h2>
                <p className="about-story-card__text">{site.about_para1 || SITE_SETTINGS_DEFAULTS.about_para1}</p>
                <p className="about-story-card__text">{site.about_para2 || SITE_SETTINGS_DEFAULTS.about_para2}</p>
                <p className="about-story-card__text">
                  {site.about_para3 || SITE_SETTINGS_DEFAULTS.about_para3}
                </p>
                {!storyImage && tagline ? (
                  <p className="about-story-card__tagline">&ldquo;{tagline}&rdquo;</p>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="container store-page__container">
        <section className="about-values">
          <div className="section-header text-center mb-4">
            <h2 className="section-title mb-2">{aboutCopy.values_title}</h2>
            <p className="section-subtitle mb-0">{aboutCopy.values_subtitle}</p>
          </div>
          <div className="row g-4">
            {values.map((v, i) => (
              <div className="col-md-4 d-flex" key={`value-${i}`}>
                <div className="value-card w-100">
                  <div className="value-icon" aria-hidden="true">
                    {v.icon}
                  </div>
                  <h3 className="value-title">{v.title}</h3>
                  <p className="value-desc mb-0">{v.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="about-page-cta text-center">
          <p className="about-page-cta-lead mb-3">{aboutCopy.cta_lead}</p>
          <Link to="/contact" className="btn btn-primary btn-lg me-sm-2 d-inline-block mb-2 mb-sm-0">
            {aboutCopy.cta_primary_label}
          </Link>
          <Link to="/shop" className="btn btn-outline-secondary btn-lg d-inline-block ms-sm-2">
            {aboutCopy.cta_secondary_label}
          </Link>
        </section>
      </div>
    </div>
  );
}

export default About;
